import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AIProviderId } from "../../shared/aiProviders";
import { IMAGE_TYPES } from "../../shared/attachmentPolicy";
import {
  attachmentSizeLimit,
  MAX_ATTACHMENTS,
  type PendingAttachment,
  resolveAttachmentMediaType,
} from "../lib/attachments";
import { emitErrorToast } from "../lib/errorToast";

function rejectionToast(message: string) {
  emitErrorToast({ icon: "paperclip", message, buttonLabel: "Dismiss" });
}

function sizeLimitLabel(mediaType: string): string {
  const max = attachmentSizeLimit(mediaType);
  const limitMb = Math.floor((max / (1024 * 1024)) * 10) / 10;
  const kind = IMAGE_TYPES.has(mediaType)
    ? "Images"
    : mediaType === "application/pdf"
      ? "PDFs"
      : "Files of this type";
  return `${kind} must be under ${limitMb} MB.`;
}

export function useFileDropzone({
  enabled = true,
  providerId,
  onZipFile,
}: {
  enabled?: boolean;
  providerId: AIProviderId;
  onZipFile?: (file: File) => void;
}) {
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const pendingRef = useRef(pendingAttachments);
  pendingRef.current = pendingAttachments;
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [flyingFiles, setFlyingFiles] = useState<
    { id: string; x: number; y: number }[]
  >([]);

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const accepted: { att: PendingAttachment; file: File }[] = [];
      let hitLimit = false;
      for (const file of Array.from(files)) {
        if (pendingRef.current.length + accepted.length >= MAX_ATTACHMENTS) {
          hitLimit = true;
          break;
        }
        const mediaType = resolveAttachmentMediaType(file, providerId);
        if (!mediaType) {
          rejectionToast(
            `${file.name} can’t be attached — that file type isn’t supported.`
          );
          continue;
        }
        if (file.size > attachmentSizeLimit(mediaType)) {
          const actualMb = Math.round((file.size / (1024 * 1024)) * 10) / 10;
          rejectionToast(
            `${file.name} is too large (${actualMb} MB). ${sizeLimitLabel(mediaType)}`
          );
          continue;
        }
        const isDuplicate = [
          ...pendingRef.current,
          ...accepted.map((a) => a.att),
        ].some(
          (a) =>
            a.fileName === file.name &&
            a.sizeBytes === file.size &&
            a.mediaType === mediaType
        );
        if (isDuplicate) continue;
        accepted.push({
          att: {
            id: nanoid(12),
            fileName: file.name,
            mediaType,
            sizeBytes: file.size,
            data: null,
            previewUrl: mediaType.startsWith("image/")
              ? URL.createObjectURL(file)
              : null,
          },
          file,
        });
      }
      if (hitLimit) {
        rejectionToast(
          `You can attach up to ${MAX_ATTACHMENTS} files per message.`
        );
      }
      if (accepted.length === 0) return;
      setPendingAttachments((prev) => [...prev, ...accepted.map((a) => a.att)]);
      for (const { att, file } of accepted) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1] ?? "";
          setPendingAttachments((prev) =>
            prev.map((a) => (a.id === att.id ? { ...a, data: base64 } : a))
          );
        };
        reader.readAsDataURL(file);
      }
    },
    [providerId]
  );

  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setPendingAttachments((prev) => {
      for (const att of prev) {
        if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
      }
      return [];
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) setIsDragging(true);
      }
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) setIsDragging(false);
    };
    const onDragOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (!files.length) return;
      if (onZipFile) {
        const zip = files.find((f) => f.name.toLowerCase().endsWith(".zip"));
        if (zip) {
          onZipFile(zip);
          return;
        }
      }
      const hasValid = files.some(
        (f) => resolveAttachmentMediaType(f, providerId) != null
      );
      if (hasValid) {
        setFlyingFiles((prev) => [
          ...prev,
          { id: nanoid(6), x: e.clientX, y: e.clientY },
        ]);
      }
      processFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [enabled, processFiles, providerId, onZipFile]);

  const removeFlyingFile = useCallback((id: string) => {
    setFlyingFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const files: File[] = [];
      for (const item of items) {
        if (item.kind !== "file") continue;
        const file = item.getAsFile();
        if (file && resolveAttachmentMediaType(file, providerId) != null) {
          files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        processFiles(files);
      }
    },
    [processFiles, providerId]
  );

  return {
    pendingAttachments,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  };
}
